import { Observable, OperatorFunction } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';

import { EntryPass} from '../../../../../../models/entry-pass';
import { EventPricingMatrix } from '../../../../../../models/event-type';
import {
  getMembershipIntervalDisplay, getMembershipDurationDisplay,
  Membership,
  MembershipPerCustomerAddOn, getMembershipPurchaseTypeDisplay
} from '../../../../../../models/membership';
import {
  AccountingConnection,
  getAccountingSoftwareDisplay, OrganizationConfig,
  OrganizationConfigRevenueAccount
} from '../../../../../../models/organization-config';

import { AccountService } from '../../../../../../services/account/account.service';
import { EntryPassService } from '../../../../../../services/entry-pass/entry-pass.service';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { MembershipService } from '../../../../../../services/membership/membership.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';

import { CallbackResult, CallbackResultStatus, TimeZones } from '../../../../../../utils';

import { SetQuickbooksDepositAccountModalComponent } from './modals/set-quickbooks-deposit-account-modal/set-quickbooks-deposit-account-modal.component';
import { ConfigureRevenueAccountsModalComponent } from './modals/configure-revenue-accounts-modal/configure-revenue-accounts-modal.component';
import { SetXeroDepositAccountModalComponent } from './modals/set-xero-deposit-account-modal/set-xero-deposit-account-modal.component';
import { SetPaymentProcessorFeeAccountModalComponent } from './modals/set-payment-processor-fee-account-modal/set-payment-processor-fee-account-modal.component';
import { VoucherPackService } from '../../../../../../services/voucher-pack/voucher-pack.service';
import { VoucherPack, VoucherPackStatus } from '../../../../../../models/voucher-pack';
import { EntryPassStatus } from '../../../../../../models/entry-pass-enums';
import { MembershipPurchaseType, MembershipStatus } from '../../../../../../models/membership-enums';
import { AccountingSoftwareType, PaymentProcessorFeePolicy } from '../../../../../../models/organization-config-enums';

class PricingMatrixWithEventType {
  eventTypeName: string;
  pricingMatrix: EventPricingMatrix;
}


@Component({
  selector: 'app-configure-accounting-connection',
  templateUrl: './configure-accounting-connection.component.html',
  styleUrls: ['./configure-accounting-connection.component.css']
})
export class ConfigureAccountingConnectionComponent implements OnInit {

  public conn: AccountingConnection;
  public orgConfig: OrganizationConfig;
  public memberships: Membership[] = [];
  public entryPasses: EntryPass[] = [];
  public pricingMatricesWithEventTypes: PricingMatrixWithEventType[] = [];
  public voucherPacks: VoucherPack[] = [];
  public originalConnName: string;

  public membershipFilter = '';
  public entryPassFilter = '';
  public eventPricingMatrixFilter = '';
  public voucherPackFilter = '';

  public loadingConn = true;
  public loadingOrgConfig = true;
  public loadingMemberships = true;
  public loadingEntryPasses = true;
  public loadingEventPricingMatrices = true;
  public loadingVoucherPacks = true;
  public loadingEdit = false;
  public loadingDepositAccount = false;
  public loadingAccountingTimeZone = false;

  public connNotFound = false;
  public serverError: number = null;

  public successMessage = '';
  public errorMessage = '';
  public errors: any = {};
  public accountingTimeZoneSuccessMessage = '';
  public accountingTimeZoneErrorMessage = '';

  public modalResult: CallbackResult = null;

  public AccountingSoftwareType = AccountingSoftwareType;
  public PaymentProcessorFeePolicy = PaymentProcessorFeePolicy;
  public CallbackResultStatus = CallbackResultStatus;
  public getAccountingSoftwareDisplay = getAccountingSoftwareDisplay;

  // Counters tracking the number of requests. Used to determine if a given
  // request is the last outstanding one of its type, which in turn determines
  // when loading indicators may be removed and data may be updated.
  private overallRequestCount = 0;
  private orgConfigRequestCount = 0;

  constructor(
    private route: ActivatedRoute,
    private accountService: AccountService,
    private orgConfigService: OrganizationConfigService,
    private membershipService: MembershipService,
    private entryPassService: EntryPassService,
    private eventTypeService: EventTypeService,
    private voucherPackService: VoucherPackService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().is_organization_admin) {
      return;
    }

    const routeParams = this.route.snapshot.params;

    const connId = parseInt(routeParams.id, 10);
    if (isNaN(connId)) {
      this.connNotFound = true;
      return;
    }

    this.loadConn(connId);
    this.loadOrgConfig();
    this.loadMemberships();
    this.loadEntryPasses();
    this.loadEventPricingMatrices();
    this.loadVoucherPacks();
  }

  public loadConn(connId: number) {
    this.loadingConn = true;

    this.orgConfigService
      .getAccountingConnection(connId)
      .then(result => {
        this.conn = result;
        this.originalConnName = this.conn.name;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.connNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingConn = false;
      });
  }

  public loadOrgConfig() {
    this.loadingOrgConfig = true;

    this.orgConfigService
      .loadOrganizationConfig()
      .then(() => {
        this.orgConfig = this.orgConfigService.getOrganizationConfig();
      })
      .finally(() => {
        this.loadingOrgConfig = false;
      });
  }

  public loadMemberships() {
    this.loadingMemberships = true;

    this.membershipService
      .getMemberships(
        1,
        100,
        {
          status: MembershipStatus.active,
          include_rolling: true,
        })
      .then(result => {
        this.memberships = result.results.filter(m => (m.purchase_type !== MembershipPurchaseType.benefit));

        // Insert empty string for any missing revenue account code values.
        for (const membership of this.memberships) {
          if (membership.revenue_account_codes[this.conn.id] === undefined) {
            membership.revenue_account_codes[this.conn.id] = '';
          }
          for (const addOn of membership.per_customer_add_ons) {
            if (addOn.revenue_account_codes[this.conn.id] === undefined) {
              addOn.revenue_account_codes[this.conn.id] = '';
            }
          }
        }
      })
      .catch()
      .finally(() => { this.loadingMemberships = false; });
  }

  public loadEntryPasses() {
    this.loadingEntryPasses = true;

    this.entryPassService
      .getEntryPasses(
        1,
        100,
        {
          status: EntryPassStatus.active
        })
      .then(result => {
        this.entryPasses = result.results;

        // Insert empty string for any missing revenue account code values.
        for (const entryPass of this.entryPasses) {
          if (entryPass.revenue_account_codes[this.conn.id] === undefined) {
            entryPass.revenue_account_codes[this.conn.id] = '';
          }
        }
      })
      .catch()
      .finally(() => { this.loadingEntryPasses = false; });
  }

  public loadEventPricingMatrices() {
    this.loadingEventPricingMatrices = true;

    this.eventTypeService
      .getAllActiveEventPricingMatrices()
      .then(result => {
        // Insert empty string for any missing revenue account code values.
        for (const eventType of result) {
          for (const pricingMatrix of eventType.pricing_matrices) {
            for (const fee of pricingMatrix.per_participant_fees) {
              if (fee.revenue_account_codes === undefined) {
                fee.revenue_account_codes = {};
              }
              if (fee.revenue_account_codes[this.conn.id] === undefined) {
                fee.revenue_account_codes[this.conn.id] = '';
              }
            }
            for (const fee of pricingMatrix.per_booking_fees) {
              if (fee.revenue_account_codes === undefined) {
                fee.revenue_account_codes = {};
              }
              if (fee.revenue_account_codes[this.conn.id] === undefined) {
                fee.revenue_account_codes[this.conn.id] = '';
              }
            }
            for (const fee of pricingMatrix.per_participant_add_ons) {
              if (fee.revenue_account_codes === undefined) {
                fee.revenue_account_codes = {};
              }
              if (fee.revenue_account_codes[this.conn.id] === undefined) {
                fee.revenue_account_codes[this.conn.id] = '';
              }
            }
            for (const fee of pricingMatrix.per_booking_add_ons) {
              if (fee.revenue_account_codes === undefined) {
                fee.revenue_account_codes = {};
              }
              if (fee.revenue_account_codes[this.conn.id] === undefined) {
                fee.revenue_account_codes[this.conn.id] = '';
              }
            }
            this.pricingMatricesWithEventTypes.push({
              eventTypeName: eventType.name,
              pricingMatrix
            });
          }
        }
      })
      .catch()
      .finally(() => { this.loadingEventPricingMatrices = false; });
  }

  public loadVoucherPacks() {
    this.loadingVoucherPacks = true;

    this.voucherPackService
      .getVoucherPacks(
        1,
        3000,
        {
          status: VoucherPackStatus.active
        })
      .then(result => {
        this.voucherPacks = result.results;

        // Insert empty string for any missing revenue account code values.
        for (const voucherPack of this.voucherPacks) {
          if (voucherPack.revenue_account_codes[this.conn.id] === undefined) {
            voucherPack.revenue_account_codes[this.conn.id] = '';
          }
        }
      })
      .catch()
      .finally(() => { this.loadingVoucherPacks = false; });
  }

  public handleResyncAccountCodes() {
    this.loadingConn = true;

    this.orgConfigService
      .syncRevenueAccounts(this.conn)
      .then(() => {
        this.loadConn(this.conn.id);
      })
      .catch(reason => {
        this.errorMessage = 'Error resyncing account codes: ' + reason.status.toString();
        this.loadingConn = false;
      });
  }

  public isUnknownRevenueAccountCode(accountCode?: string) {
    if (!accountCode) {
      return false;
    }

    for (const account of this.conn.revenue_accounts) {
      if (account.account_id === accountCode) {
        return false;
      }
    }
    return true;
  }

  public getMembershipTypeString(membership: Membership): string {
    let typeString = '';

    if (membership.is_group) {
      typeString = 'Group, ';
    } else {
      typeString = 'Single, ';
    }

    typeString += getMembershipPurchaseTypeDisplay(membership.purchase_type) + ', ';

    if (membership.interval !== '') {
      typeString += getMembershipIntervalDisplay(membership.interval);
    } else if (membership.has_fixed_date_ranges) {
      typeString += 'Fixed Date Ranges';
    } else {
      typeString += getMembershipDurationDisplay(
        membership.duration_years,
        membership.duration_months,
        membership.duration_days
      );
    }

    return typeString;
  }

  public getFilteredMemberships(): Membership[] {
    if (this.membershipFilter === '') {
      return this.memberships;
    }

    const filterLower = this.membershipFilter.toLowerCase();

    return this.memberships.filter((membership) => {
      return (
        (membership.name.toLowerCase().indexOf(filterLower) !== -1) ||
        (this.getMembershipTypeString(membership).toLowerCase().indexOf(filterLower) !== -1)
      );
    });
  }

  public getFilteredEntryPasses(): EntryPass[] {
    if (this.entryPassFilter === '') {
      return this.entryPasses;
    }

    const filterLower = this.entryPassFilter.toLowerCase();

    return this.entryPasses.filter((entryPass) => {
      return (
        (entryPass.name.toLowerCase().indexOf(filterLower) !== -1) ||
        (entryPass.num_passes.toString().indexOf(filterLower) !== -1)
      );
    });
  }

  public getFilteredEventPricingMatrices(): PricingMatrixWithEventType[] {
    if (this.eventPricingMatrixFilter === '') {
      return this.pricingMatricesWithEventTypes;
    }

    const filterLower = this.eventPricingMatrixFilter.toLowerCase();

    return this.pricingMatricesWithEventTypes.filter((pricingMatrixWithEventType) => {
      return (
        (pricingMatrixWithEventType.eventTypeName.toLowerCase().indexOf(filterLower) !== -1) ||
        (pricingMatrixWithEventType.pricingMatrix.name.toLowerCase().indexOf(filterLower) !== -1)
      );
    });
  }

  public getFilteredVoucherPacks(): VoucherPack[] {
    if (this.voucherPackFilter === '') {
      return this.voucherPacks;
    }

    const filterLower = this.voucherPackFilter.toLowerCase();

    return this.voucherPacks.filter((voucherPack) => {
      return (
        (voucherPack.name.toLowerCase().indexOf(filterLower) !== -1) ||
        (voucherPack.number_of_vouchers.toString().indexOf(filterLower) !== -1)
      );
    });
  }

  public saveConn() {
    this.loadingEdit = true;

    this.successMessage = '';

    this.overallRequestCount++;
    this.orgConfigRequestCount++;

    const thisOverallRequestCount = this.overallRequestCount;
    const thisSaveConnRequestCount = this.orgConfigRequestCount;

    this.orgConfigService
      .updateAccountingConnection(this.conn)
      .then(response => {
        // Update the local org config if there are no other org config requests in progress.
        if (thisSaveConnRequestCount === this.orgConfigRequestCount) {
          this.conn = response;
          this.originalConnName = this.conn.name;
        }
        if (thisOverallRequestCount === this.overallRequestCount) {
          this.successMessage = 'Changes saved successfully.';
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to update this configuration value.';
        } else {
          this.errorMessage = 'Error updating config: ' + reason.status.toString();
        }
      })
      .finally(() => {
        if (thisOverallRequestCount === this.overallRequestCount) {
          this.loadingEdit = false;
        }
      });
  }

  public updateMembershipRevenueAccount(membership: Membership) {
    this.loadingEdit = true;

    this.successMessage = '';
    this.errorMessage = '';

    this.overallRequestCount++;

    const thisOverallRequestCount = this.overallRequestCount;

    this.membershipService
      .updateMembershipRevenueAccount(this.conn, membership, membership.revenue_account_codes[this.conn.id])
      .then(() => {
        if (thisOverallRequestCount === this.overallRequestCount) {
          this.successMessage = 'Changes saved successfully.';
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to update this configuration value.';
        } else {
          this.errorMessage = 'Error updating config: ' + reason.status.toString();
        }
        // Reload the memberships to ensure they're in sync.
        this.loadMemberships();
      })
      .finally(() => {
        if (thisOverallRequestCount === this.overallRequestCount) {
          this.loadingEdit = false;
        }
      });
  }

  public updateMembershipAddOnRevenueAccount(membership: Membership, addOn: MembershipPerCustomerAddOn) {
    this.loadingEdit = true;

    this.successMessage = '';
    this.errorMessage = '';

    this.overallRequestCount++;

    const thisOverallRequestCount = this.overallRequestCount;

    this.membershipService
      .updateMembershipAddOnRevenueAccount(this.conn, membership, addOn, addOn.revenue_account_codes[this.conn.id])
      .then(() => {
        if (thisOverallRequestCount === this.overallRequestCount) {
          this.successMessage = 'Changes saved successfully.';
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to update this configuration value.';
        } else {
          this.errorMessage = 'Error updating config: ' + reason.status.toString();
        }
        // Reload the memberships to ensure they're in sync.
        this.loadMemberships();
      })
      .finally(() => {
        if (thisOverallRequestCount === this.overallRequestCount) {
          this.loadingEdit = false;
        }
      });
  }

  public updateEntryPassRevenueAccount(entryPass: EntryPass) {
    this.loadingEdit = true;

    this.successMessage = '';
    this.errorMessage = '';

    this.overallRequestCount++;

    const thisOverallRequestCount = this.overallRequestCount;

    this.entryPassService
      .updateEntryPassRevenueAccount(this.conn, entryPass, entryPass.revenue_account_codes[this.conn.id])
      .then(() => {
        if (thisOverallRequestCount === this.overallRequestCount) {
          this.successMessage = 'Changes saved successfully.';
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to update this configuration value.';
        } else {
          this.errorMessage = 'Error updating config: ' + reason.status.toString();
        }
        // Reload the entry passes to ensure they're in sync.
        this.loadEntryPasses();
      })
      .finally(() => {
        if (thisOverallRequestCount === this.overallRequestCount) {
          this.loadingEdit = false;
        }
      });
  }

  public updatePricingMatrixRevenueAccount(pricingMatrix: EventPricingMatrix) {
    this.loadingEdit = true;

    this.successMessage = '';
    this.errorMessage = '';

    this.overallRequestCount++;

    const thisOverallRequestCount = this.overallRequestCount;

    this.eventTypeService
      .updateEventPricingMatrix(pricingMatrix)
      .then(() => {
        if (thisOverallRequestCount === this.overallRequestCount) {
          this.successMessage = 'Changes saved successfully.';
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to update this configuration value.';
        } else {
          this.errorMessage = 'Error updating config: ' + reason.status.toString();
        }
      })
      .finally(() => {
        if (thisOverallRequestCount === this.overallRequestCount) {
          this.loadingEdit = false;
        }
      });
  }

  public updateVoucherPackRevenueAccount(voucherPack: VoucherPack) {
    this.loadingEdit = true;

    this.successMessage = '';
    this.errorMessage = '';

    this.overallRequestCount++;

    const thisOverallRequestCount = this.overallRequestCount;

    this.voucherPackService
      .updateVoucherPackRevenueAccount(this.conn, voucherPack, voucherPack.revenue_account_codes[this.conn.id])
      .then(() => {
        if (thisOverallRequestCount === this.overallRequestCount) {
          this.successMessage = 'Changes saved successfully.';
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to update this configuration value.';
        } else {
          this.errorMessage = 'Error updating config: ' + reason.status.toString();
        }
        // Reload the voucher packs to ensure they're in sync.
        this.loadVoucherPacks();
      })
      .finally(() => {
        if (thisOverallRequestCount === this.overallRequestCount) {
          this.loadingEdit = false;
        }
      });
  }

  public getRevenueAccountDisplay(revenueAccount: OrganizationConfigRevenueAccount) {
    if (revenueAccount.account_code !== '') {
      return revenueAccount.account_code + ' - ' + revenueAccount.account_name;
    } else {
      return revenueAccount.account_id + ' - ' + revenueAccount.account_name;
    }
  }

  public getDefaultRevenueAccountDisplay(defaultAcctId: string) {
    if (!defaultAcctId) {
      return 'Do Not Report';
    }

    for (const revenueAcct of this.conn.revenue_accounts) {
      if (revenueAcct.account_id === defaultAcctId){
        return this.getRevenueAccountDisplay(revenueAcct);
      }
    }

    return defaultAcctId + ' (Account not found)';
  }

  public hasDepositAccount(): boolean {
    if (this.conn.accounting_software === AccountingSoftwareType.quickbooksOnline) {
      return this.conn.quickbooks_deposit_account_id !== ''
    } else if (this.conn.accounting_software === AccountingSoftwareType.xero) {
      return this.conn.xero_deposit_account_id !== ''
    } else {
      return false;
    }
  }

  public hasPaymentProcessorFeeAccount(): boolean {
    return (
      (this.conn.payment_processor_fee_account_id !== '') ||
      (this.conn.payment_processor_fee_account_code !== '') ||
      (this.conn.payment_processor_fee_account_name !== '')
    )
  }

  public openSetDepositAccountModal() {
    this.modalResult = null;

    let modalRef: NgbModalRef;
    if (this.conn.accounting_software === AccountingSoftwareType.quickbooksOnline) {
      modalRef = this.modalService.open(SetQuickbooksDepositAccountModalComponent);
    } else if (this.conn.accounting_software === AccountingSoftwareType.xero) {
      modalRef = this.modalService.open(SetXeroDepositAccountModalComponent);
    } else {
      return;
    }

    modalRef.componentInstance.connId = this.conn.id;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadConn(this.conn.id);
    };
  }

  public openSetPaymentProcessorFeeAccountModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(SetPaymentProcessorFeeAccountModalComponent);
    modalRef.componentInstance.conn = this.conn;
    modalRef.componentInstance.currentAccountId = this.conn.payment_processor_fee_account_id;
    modalRef.componentInstance.currentAccountCode = this.conn.payment_processor_fee_account_code;
    modalRef.componentInstance.currentAccountName = this.conn.payment_processor_fee_account_name;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadConn(this.conn.id);
    };
  }

  public clearDepositAccount() {
    this.loadingDepositAccount = true;

    this.orgConfigService
      .clearXeroDepositAccount(this.conn)
      .then(() => {
        this.modalResult = {
          text: 'Deposit account cleared successfully.',
          status: CallbackResultStatus.success
        };
        this.loadConn(this.conn.id);
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingDepositAccount = false; });
  }

  public openConfigureRevenueAccountsModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(ConfigureRevenueAccountsModalComponent);
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadConn(this.conn.id);
    };
    modalRef.componentInstance.conn = this.conn;
  }

  public updateAccountingTimeZone() {
    this.loadingAccountingTimeZone = true;

    this.accountingTimeZoneSuccessMessage = '';
    this.accountingTimeZoneErrorMessage = '';

    if (this.conn.time_zone) {
      this.conn.time_zone = this.conn.time_zone.trim();
    }

    if (this.conn.time_zone === '') {
      this.conn.time_zone = null;
    }

    this.orgConfigService
      .updateAccountingConnection(this.conn)
      .then(() => {
        this.accountingTimeZoneSuccessMessage = 'Accounting time zone updated successfully.';
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountingTimeZoneErrorMessage = 'You do not have permission to update this configuration value.';
        } else if (reason.status === 400) {
          this.accountingTimeZoneErrorMessage = 'Invalid time zone.'
        } else {
          this.accountingTimeZoneErrorMessage = 'Error updating config: ' + reason.status.toString();
        }
      })
      .finally(() => { this.loadingAccountingTimeZone = false; });
  }

  public handleTimeZoneTypeahead: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(
        term => term.length === 0 ? []
        : TimeZones.filter(v => v.toLowerCase().indexOf(term.toLowerCase()) > -1).slice(0, 10)
      )
    )

}
